import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { API_BASE_URL, AUTH_CODE_CONFIRMATION, AUTH_CODE_CONFIRMATION_MOB, AUTH_LOGIN_CODE } from '../../apiServices/api_routes';
import AuthService from '../../apiServices/tokenservices/auth.service';
import styles from './css/Authenticate.module.css';
import { GetMyWorkspaces, GetUserData, setAccessToken, setWorkspaceSelectionRequired } from '../../redux/usersSlice';
import { setProjectImg } from '../../redux/projectDataSlice';
import { SpinnerIos16Regular } from '@fluentui/react-icons';

import { setDocs } from '../../redux/documentsSlice';
import Logo from '../../assets/longLogoBright.png';
import Cookies from 'js-cookie';
import { isObject } from '../../utils/commonUtils';
import { writePendingSsoMfaState, readPendingSsoMfaState, clearPendingSsoMfaState } from '../../utils/pendingSsoMfa';
import { stashInviteKeyFromSearch, consumePendingInviteKey } from '../../utils/pendingInviteKey';
import { getReturnPathFromLocation, clearPostAuthReturn } from '../../utils/postAuthRedirect';

const SUPPORT_EMAIL = 'LensSupport@beyond-ma.com';

const Authenticate = () => {
    var promiseToast = null;
    const location = useLocation();
    const [buttonLoading, setButtonLoading] = React.useState(false);
    const [resendSecondsRemaining, setResendSecondsRemaining] = React.useState(30);
    const config = {
        headers: {
            Authorization: `Bearer ${location?.state?.access}`,
        },
    };
    const user = useSelector(state => state?.user);
    const dispatch = useDispatch();

    const navigate = useNavigate();

    useEffect(() => {
        const tokenFromLocation = location.state?.ephemeral_token;
        if (tokenFromLocation) {
            writePendingSsoMfaState(location.state);
            return;
        }
        const stored = readPendingSsoMfaState();
        if (stored?.ephemeral_token) {
            navigate('/authenticate', { state: stored, replace: true });
        }
    }, [location.state, navigate]);

    const [ephToken, setEphToken] = React.useState(location?.state?.ephemeral_token);
    const [rememberMe, setRememberMe] = React.useState(false);
    const type = location?.state?.type;
    const [otp, setOTP] = React.useState(['', '', '', '', '', '']);
    const [mfaLockoutSecondsRemaining, setMfaLockoutSecondsRemaining] = React.useState(0);
    const [showMfaLockScreen, setShowMfaLockScreen] = React.useState(false);
    const previousMfaLockoutSecondsRef = React.useRef(0);
    const inputRefs = React.useRef([]);
    const isMfaLocked = mfaLockoutSecondsRemaining > 0;
    const mfaLockoutMinutesRemaining = Math.floor(mfaLockoutSecondsRemaining / 60);
    const mfaLockoutSecondsDisplay = mfaLockoutSecondsRemaining % 60;
    const mfaLockoutCountdownText = `${mfaLockoutMinutesRemaining}:${String(mfaLockoutSecondsDisplay).padStart(2, '0')}`;
    const isResendDisabled = resendSecondsRemaining > 0;

    const routeAfterLogin = async () => {
        stashInviteKeyFromSearch(location.search);
        const pendingInviteKey = consumePendingInviteKey();
        if (pendingInviteKey) {
            navigate(`/validate-invite/${pendingInviteKey}`, { replace: true });
            return;
        }

        const returnPath = getReturnPathFromLocation(location);
        if (returnPath) {
            clearPostAuthReturn();
            navigate(returnPath, { replace: true });
            return;
        }

        const res = await dispatch(GetMyWorkspaces());
        const memberships = res?.payload?.data?.memberships || [];

        if (!memberships.length) {
            navigate('/no-workspace-access', { replace: true });
            return;
        }

        if (memberships.length === 1) {
            dispatch(setWorkspaceSelectionRequired(false));
            navigate('/project', { replace: true });
            return;
        }

        dispatch(setWorkspaceSelectionRequired(true));
        navigate('/select-workspace', { replace: true });
    };

    useEffect(() => {
        if (!isMfaLocked) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            setMfaLockoutSecondsRemaining(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, [isMfaLocked]);

    useEffect(() => {
        if (resendSecondsRemaining <= 0) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            setResendSecondsRemaining(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, [resendSecondsRemaining]);

    useEffect(() => {
        const previousSeconds = previousMfaLockoutSecondsRef.current;
        const lockoutJustExpired = previousSeconds > 0 && mfaLockoutSecondsRemaining === 0 && showMfaLockScreen;

        if (lockoutJustExpired) {
            toast.success('Your temporary sign-in restriction has been lifted. Please continue to sign in.', {
                autoClose: 10000,
            });
        }

        previousMfaLockoutSecondsRef.current = mfaLockoutSecondsRemaining;
    }, [mfaLockoutSecondsRemaining, showMfaLockScreen]);

    useEffect(() => {
        if (location?.state?.ephemeral_token) {
            setEphToken(location.state.ephemeral_token);
        }
    }, [location.state?.ephemeral_token]);

    const handleBackToSignIn = () => {
        if (isMfaLocked) {
            return;
        }
        setShowMfaLockScreen(false);
        setMfaLockoutSecondsRemaining(0);
        navigate('/login', { replace: true });
    };

    const handleSubmit = async () => {
        if (isMfaLocked || showMfaLockScreen) {
            return;
        }
        setButtonLoading(true);
        const verificationCode = otp.join('');
        promiseToast = toast.loading('Verifying Code...', {
            closeButton: true,
        });

        if (location?.state?.ephemeral_token) {
            handleAuth();
        } else {
            const { email, password } = location?.state;

            // If we have email and password, try regular login
            if (email && password) {
                try {
                    if (type === 'sms_twilio') {
                        await axios.post(`${API_BASE_URL}${AUTH_CODE_CONFIRMATION_MOB}`, { code: verificationCode }, config);
                    } else {
                        await axios.post(`${API_BASE_URL}${AUTH_CODE_CONFIRMATION}`, { code: verificationCode }, config);
                    }
                } catch (error) {
                    toast.update(promiseToast, {
                        render: 'The verification code entered is incorrect. Please check the code and try again.',
                        type: 'error',
                        isLoading: false,
                        autoClose: 1000,
                    });
                    setButtonLoading(false);
                }
                try {
                    const res = await AuthService.login(email, password);
                    if (res?.ephemeral_token) {
                        setEphToken(res?.ephemeral_token);
                        handleAuth(res?.ephemeral_token);
                    }
                } catch (error) {
                    toast.update(promiseToast, {
                        render: 'The verification code entered is incorrect. Please check the code and try again.',
                        type: 'error',
                        isLoading: false,
                        autoClose: 1000,
                    });
                    setButtonLoading(false);
                }
            } else {
                // For Azure SSO, we should already have the ephemeral token
                toast.update(promiseToast, {
                    render: 'No authentication data available',
                    type: 'error',
                    isLoading: false,
                    autoClose: 1000,
                });
                setButtonLoading(false);
            }
        }
    };

    const handleMfaLockoutError = error => {
        const statusCode = error?.response?.status;
        const data = error?.response?.data;
        if (statusCode !== 423 || !isObject(data)) {
            return false;
        }

        const seconds = Number(data?.retry_after_seconds);
        const remaining = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 5 * 60;
        setMfaLockoutSecondsRemaining(remaining);
        setShowMfaLockScreen(true);
        setButtonLoading(false);
        toast.update(promiseToast, {
            render: 'Please wait 5 miniutes before trying again.',
            type: 'error',
            isLoading: false,
            autoClose: 10000,
        });
        return true;
    };
    const handlePaste = event => {
        const pastedData = event.clipboardData.getData('Text');
        if (pastedData.length === 6 && inputRefs.current[0] === document.activeElement) {
            // If pasted data length is 6 and the first input is focused, update the OTP
            const newOTP = pastedData.split('').slice(0, 6);
            setOTP(newOTP);

            // Focus on the next input field
            inputRefs.current[1].focus();
        }
    };
    const handleAuth = async token => {
        const verificationCode = otp.join('');

        // Check if this is Azure SSO (no email/password in state)
        const isAzureSSO = !location?.state?.email && !location?.state?.password;

        console.log('[DEBUG] Is Azure SSO:', isAzureSSO);
        console.log('[DEBUG] Location state:', location?.state);

        if (isAzureSSO) {
            // Use Azure SSO MFA endpoint
            const requestData = {
                code: verificationCode,
                ephemeral_token: token || ephToken,
                remember_me: rememberMe,
            };

            console.log('[DEBUG] Submitting Azure SSO MFA code with data:', requestData);
            console.log('[DEBUG] Ephemeral token:', token || ephToken);

            try {
                const res = await axios.post(`${API_BASE_URL}/accounts/auth/azure-sso-mfa-verify/`, requestData);
                console.log('[DEBUG] Azure SSO MFA response:', res.data);

                setButtonLoading(false);
                dispatch(setAccessToken(res?.data?.access));
                Cookies.set('refresh', res?.data?.refresh, {
                    secure: true,
                    sameSite: 'strict',
                });
                clearPendingSsoMfaState();

                await dispatch(GetUserData());
                toast.update(promiseToast, {
                    render: 'Done',
                    type: 'success',
                    isLoading: false,
                    autoClose: 1000,
                });
                await routeAfterLogin();
            } catch (error) {
                console.error('[DEBUG] Azure SSO MFA submission error:', error);
                console.error('[DEBUG] Error response:', error.response?.data);
                console.error('[DEBUG] Error status:', error.response?.status);

                if (handleMfaLockoutError(error)) {
                    return;
                }

                toast.update(promiseToast, {
                    render: 'The verification code entered is incorrect. Please check the code and try again.',
                    type: 'error',
                    isLoading: false,
                    autoClose: 1000,
                });
                setButtonLoading(false);
            }
        } else {
            // Use regular Django Trench MFA endpoint
            const requestData = {
                code: verificationCode,
                ephemeral_token: token || ephToken,
                remember_me: rememberMe,
            };

            console.log('[DEBUG] Submitting regular MFA code with data:', requestData);
            console.log('[DEBUG] Ephemeral token:', token || ephToken);

            try {
                const res = await axios.post(`${API_BASE_URL}${AUTH_LOGIN_CODE}`, requestData);
                console.log('[DEBUG] Regular MFA response:', res.data);

                setButtonLoading(false);
                dispatch(setAccessToken(res?.data?.access));
                Cookies.set('refresh', res?.data?.refresh, {
                    secure: true,
                    sameSite: 'strict',
                });
                clearPendingSsoMfaState();

                await dispatch(GetUserData());
                toast.update(promiseToast, {
                    render: 'Done',
                    type: 'success',
                    isLoading: false,
                    autoClose: 1000,
                });
                await routeAfterLogin();
            } catch (error) {
                console.error('[DEBUG] Regular MFA submission error:', error);
                console.error('[DEBUG] Error response:', error.response?.data);
                console.error('[DEBUG] Error status:', error.response?.status);

                if (handleMfaLockoutError(error)) {
                    return;
                }

                toast.update(promiseToast, {
                    render: 'The verification code entered is incorrect. Please check the code and try again.',
                    type: 'error',
                    isLoading: false,
                    autoClose: 1000,
                });
                setButtonLoading(false);
            }
        }
    };

    const logoutUser = React.useCallback(() => {
        clearPendingSsoMfaState();
        localStorage.removeItem('persist:root');
        Cookies.set('refresh', '');
        localStorage.clear();
        dispatch(setProjectImg(''));
        dispatch(setDocs([]));
        navigate('/');
    }, [dispatch, navigate]);
    const isFilled = otp.every(digit => digit !== '');
    const handleChange = (index, event) => {
        const newOTP = [...otp];
        newOTP[index] = event.target.value;
        setOTP(newOTP);

        // Move to the next input field
        if (event.target.value && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };
    const handleKeyDown = (index, event) => {
        // Move to the previous input field if backspace is pressed and the current input field is empty
        if (event.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handleResendCode = async () => {
        if (isResendDisabled) {
            return;
        }

        const pending = readPendingSsoMfaState();
        const email = location?.state?.email ?? pending?.email;
        const password = location?.state?.password ?? pending?.password;
        const token =
            ephToken || location?.state?.ephemeral_token || pending?.ephemeral_token;
        const mfaType = type || pending?.type || location?.state?.type;

        // SSO passes email/password as empty strings; password login has real credentials.
        const hasPasswordCreds = Boolean(email && password);

        if (token && !hasPasswordCreds) {
            if (mfaType !== 'sms_twilio') {
                toast.info('Open your authenticator app — a new code is generated every few seconds.');
                return;
            }
            setResendSecondsRemaining(30);
            try {
                await axios.post(`${API_BASE_URL}/accounts/auth/azure-sso-mfa-resend/`, {
                    ephemeral_token: token,
                });
                toast.success('Verification code resent to your registered mobile number.');
            } catch (error) {
                const data = error?.response?.data;
                const msg =
                    (typeof data?.error === 'string' && data.error) ||
                    (typeof data?.detail === 'string' && data.detail) ||
                    'Failed to resend code';
                toast.error(msg);
            }
            return;
        }

        if (!email || !password) {
            toast.error('Cannot resend code. Please try logging in again.');
            return;
        }

        setResendSecondsRemaining(30);
        try {
            await AuthService.login(email?.toLowerCase(), password);
            toast.success('Verification code resent to registered mobile number.');
        } catch (error) {
            toast.error('Failed to resend code');
        }
    };
    // Do not send users to /project while MFA is in progress (SSO + Twilio or password login step).
    // Stale persisted isLoggedIn can otherwise skip the OTP screen and bounce them (e.g. back to login).
    useEffect(() => {
        const hasMfaStep =
            Boolean(location?.state?.ephemeral_token) || Boolean(readPendingSsoMfaState()?.ephemeral_token);
        if (user?.isLoggedIn && !hasMfaStep) {
            navigate('/project');
        }
    }, [user?.isLoggedIn, navigate, location?.state?.ephemeral_token]);
    // Long timeout so SSO / SMS users are not kicked back to login while waiting for OTP (was 4 minutes).
    useEffect(() => {
        const timer = setTimeout(() => {
            logoutUser();
        }, 30 * 60 * 1000);
        return () => clearTimeout(timer);
    }, [logoutUser]);
    return (
        <div className={styles.body}>
            <div className={styles.dotedBg}>
                <div className={styles.formCard}>
                    <div className={styles.formHeader}>
                        <img src={Logo} width={73} alt="lens-logo" />
                    </div>
                    <div className={styles.formBody}>
                        {showMfaLockScreen ? (
                            <div className={styles.lockedContainer}>
                                <h5 className={styles.lockedTitle}>Your Account Is Temporarily Locked</h5>
                                <p className={styles.lockedMessage}>
                                    For security reasons, your account has been temporarily locked after multiple unsuccessful verification attempts. Please try signing in again in{' '}
                                    {mfaLockoutCountdownText}.
                                </p>
                                <div className={styles.lockedActions}>
                                    <button
                                        type="button"
                                        disabled={isMfaLocked}
                                        onClick={handleBackToSignIn}
                                        className={`${styles.submitBtn} ${isMfaLocked ? styles.lockedSignInBtn : ''}`}>
                                        Sign-in
                                    </button>
                                    <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.secondaryBtnLink}>
                                        Contact Support
                                    </a>
                                </div>
                            </div>
                        ) : (
                            <>
                                <h5 className={styles.formLabel}>2FA Authentication</h5>
                                <p className={styles.formSubtitle}>
                                    {type === 'sms_twilio' ? 'Enter 2FA code sent to your registered mobile number.' : 'Enter 2FA code from your authenticator app.'}
                                </p>
                                <div className={styles.otpField}>
                                    {otp.map((digit, index) => (
                                        <input
                                            key={index}
                                            type="text"
                                            maxLength="1"
                                            value={digit}
                                            onChange={event => handleChange(index, event)}
                                            onKeyDown={event => handleKeyDown(index, event)}
                                            ref={ref => (inputRefs.current[index] = ref)}
                                            onPaste={event => handlePaste(event)}
                                        />
                                    ))}
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div onClick={() => setRememberMe(!rememberMe)} role="button" className={styles.rememberMeSection}>
                                        <input checked={rememberMe} type="checkbox" />
                                        <label className={styles.rememberBeText}>Remember this device for 15 days</label>
                                    </div>
                                    {type === 'sms_twilio' && (
                                        <div>
                                            <span
                                                style={{ color: isResendDisabled && 'var(--Neutral-Gray-6)', pointerEvents: isResendDisabled && 'none' }}
                                                onClick={handleResendCode}
                                                className={styles.secondaryBtnLink}>
                                                {isResendDisabled ? `Resend (${resendSecondsRemaining})` : 'Resend'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="d-flex align-items-center justify-content-between">
                                    <button disabled={buttonLoading || !isFilled} onClick={handleSubmit} className={styles.submitBtn}>
                                        Sign in
                                        {buttonLoading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                                    </button>
                                    <a href={`mailto:${SUPPORT_EMAIL}`}>
                                        <span role="button" className={styles.secondaryBtnLink}>
                                            Trouble signing in?
                                        </span>
                                    </a>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div className={styles.footer}>
                <a href="https://www.iubenda.com/terms-and-conditions/15531073" target="_blank" rel="noreferrer">
                    Terms of Use
                </a>
                <a href="https://www.iubenda.com/privacy-policy/15531073/legal?ifr=true&height=650&newmarkup=yes&an=no" target="_blank" rel="noreferrer">
                    Privacy & Cookies
                </a>
            </div>
        </div>
    );
    // return (
    //     <div className=" full-height-cls  bg-light">
    //         <Pageheader title="LENS - Authenticate" />
    //         <header className="header ">
    //             <div className={styles.navbarbrand}>
    //                 <Link to="/">
    //                     <img src={Logo} alt="logo" width="100px" height="54px" />
    //                 </Link>
    //             </div>
    //         </header>
    //         <div className="container">
    //             <div className={`row ${styles.viewHeight}`}>
    //                 <div className="col-sm-12 col-md-5">
    //                     <div className={`${styles.formBody} `}>
    //                         <div className={styles.rightContent}>
    //                             <h2 className={styles.headingText}>Enter the Authentication code</h2>
    //                             <p className={styles.emailText}>
    //                                 {type === 'sms'
    //                                     ? `We have texted you the code on (Mobile number). Please enter the code to sign in.`
    //                                     : `Please enter the code displayed on your authenticator application from your device.`}
    //                             </p>
    //                             {/* <br /> */}
    //                             <div className="row">
    //                                 <div className="col-sm-12 col-md-6">
    //                                     <input
    //                                         className="form-control commonInput"
    //                                         placeholder="Code"
    //                                         value={verificationCode}
    //                                         onChange={e => setVerificationCode(e.target.value)}
    //                                     />
    //                                 </div>
    //                                 <div className="mt-2">
    //                                     <input checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} type="checkbox" id="exampleCheck1" />
    //                                     <small className="form-check-label ms-2" htmlFor="exampleCheck1 ">
    //                                         Don’t ask again for 15 days
    //                                     </small>
    //                                 </div>
    //                                 <div className="mt-4 mb-4">
    //                                     <small>
    //                                         Having trouble?{' '}
    //                                         <span
    //                                             role="button"
    //                                             style={{ color: 'var(--Brand-Secondary)' }}
    //                                             onClick={() =>
    //                                                 navigate('/secure', {
    //                                                     state: {
    //                                                         access: location?.state?.access,
    //                                                         email: location?.state?.email,
    //                                                         password: location?.state?.password,
    //                                                     },
    //                                                 })
    //                                             }>
    //                                             {' '}
    //                                             Sign in another way.
    //                                         </span>
    //                                     </small>
    //                                 </div>
    //                                 <div className="d-flex ">
    //                                     <button onClick={handleSubmit} style={{ width: '120px' }} className="btn btn-primary">
    //                                         Verify
    //                                     </button>
    //                                 </div>
    //                             </div>
    //                         </div>
    //                     </div>
    //                 </div>
    //             </div>

    //         </div>
    //     </div>
    // );
};
export default Authenticate;
